// ============================================
// Event Reports
// ============================================

import React from 'react';
import { collection, query, where, getDocs, orderBy } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../../../config/firebase';
import { Event, Session, EventTherapist, TherapistAttendance } from '../../../types';
import { useAuth } from '../../../contexts/AuthContext';

interface Props {
  event: Event;
}

interface TherapistReport {
  uid: string;
  displayName: string;
  thumbnailUrl?: string;
  photoUrl?: string;
  sessionsCompleted: number;
  minutesDelivered: number;
  billTotal: number;
  amountReceived: number;
  tipsTotal: number;
  therapistPayout: number;
  // Rating data (only visible to admins)
  ratingCount: number;
  ratingSum: number;
  avgRating: number;
  // Time tracking
  venueMinutes: number;
  breakMinutes: number;
  workMinutes: number;  // venueMinutes - breakMinutes
  utilizationPct: number;  // sessionMinutes / workMinutes
}

interface DailyReport {
  date: string;
  displayDate: string;
  sessionsCompleted: number;
  minutesDelivered: number;
  billTotal: number;
  amountReceived: number;
  tipsTotal: number;
  eventPayout: number;
}

type ViewMode = 'daily' | 'therapist' | 'grid' | 'sessions';

type TherapistSortField = 'displayName' | 'sessionsCompleted' | 'minutesDelivered' | 'billTotal' | 'amountReceived' | 'tipsTotal' | 'therapistPayout' | 'avgRating' | 'venueMinutes' | 'breakMinutes' | 'utilizationPct';

export default function EventReports({ event }: Props) {
  const { role } = useAuth();
  const [loading, setLoading] = React.useState(true);
  const [sessions, setSessions] = React.useState<Session[]>([]);
  const [therapists, setTherapists] = React.useState<EventTherapist[]>([]);
  const [attendanceRecords, setAttendanceRecords] = React.useState<TherapistAttendance[]>([]);
  const [viewMode, setViewMode] = React.useState<ViewMode>('daily');
  const [selectedDate, setSelectedDate] = React.useState<string | null>(null);
  const [selectedTherapist, setSelectedTherapist] = React.useState<string | null>(null);
  const [allSessions, setAllSessions] = React.useState<Session[]>([]); // All sessions including active
  const [therapistSortField, setTherapistSortField] = React.useState<TherapistSortField>('therapistPayout');

  // Edit/Void session state
  const [editingSession, setEditingSession] = React.useState<Session | null>(null);
  const [editMinutes, setEditMinutes] = React.useState<number>(0);
  const [editAmountReceived, setEditAmountReceived] = React.useState<number>(0);
  const [editReason, setEditReason] = React.useState<string>('');
  const [actionInProgress, setActionInProgress] = React.useState<string | null>(null);

  const isSuperAdmin = role === 'superadmin';
  const [therapistSortDesc, setTherapistSortDesc] = React.useState(true);

  // Generate event days from start to end date
  const eventDays = React.useMemo(() => {
    const days: string[] = [];
    if (event.startDate && event.endDate) {
      const start = new Date(event.startDate);
      const end = new Date(event.endDate);
      const current = new Date(start);
      while (current <= end) {
        days.push(current.toISOString().split('T')[0]);
        current.setDate(current.getDate() + 1);
      }
    }
    return days;
  }, [event.startDate, event.endDate]);

  React.useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [event.id]);

  const loadData = async () => {
    setLoading(true);

    // Get therapists
    const therapistsSnap = await getDocs(collection(db, 'events', event.id, 'therapists'));
    const therapistsData = therapistsSnap.docs.map((doc) => ({
      uid: doc.id,
      ...doc.data(),
    })) as EventTherapist[];
    setTherapists(therapistsData);

    // Get completed sessions (for reports calculations)
    const completedSessionsQuery = query(
      collection(db, 'events', event.id, 'sessions'),
      where('status', '==', 'completed'),
      orderBy('completedAt', 'desc')
    );
    const completedSessionsSnap = await getDocs(completedSessionsQuery);
    const completedSessionsData = completedSessionsSnap.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    })) as Session[];
    setSessions(completedSessionsData);

    // Get all sessions (including active) for session log
    const allSessionsQuery = query(
      collection(db, 'events', event.id, 'sessions'),
      orderBy('startedAt', 'desc')
    );
    const allSessionsSnap = await getDocs(allSessionsQuery);
    const allSessionsData = allSessionsSnap.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    })) as Session[];
    setAllSessions(allSessionsData);

    // Get attendance records for all therapists
    const allAttendance: TherapistAttendance[] = [];
    for (const therapist of therapistsData) {
      const attendanceSnap = await getDocs(
        collection(db, 'events', event.id, 'therapists', therapist.uid, 'attendance')
      );
      attendanceSnap.docs.forEach((doc) => {
        allAttendance.push({
          ...doc.data(),
          therapistId: therapist.uid,
        } as TherapistAttendance);
      });
    }
    setAttendanceRecords(allAttendance);

    setLoading(false);
  };

  // Open edit modal
  const openEditModal = (session: Session) => {
    setEditingSession(session);
    setEditMinutes(session.totalMinutes);
    setEditAmountReceived(session.amountReceived || 0);
    setEditReason('');
  };

  // Save edited session
  const saveEditedSession = async () => {
    if (!editingSession || !editReason.trim()) {
      alert('Please provide a reason for the edit');
      return;
    }

    setActionInProgress(`edit-${editingSession.id}`);
    try {
      const editSession = httpsCallable(functions, 'adminEditSession');
      await editSession({
        eventId: event.id,
        sessionId: editingSession.id,
        newTotalMinutes: editMinutes,
        newAmountReceived: editAmountReceived,
        reason: editReason,
      });
      alert('Session updated successfully');
      setEditingSession(null);
      await loadData(); // Refresh data
    } catch (err: any) {
      console.error('Error editing session:', err);
      alert(err.message || 'Failed to edit session');
    } finally {
      setActionInProgress(null);
    }
  };

  // Void a session
  const voidSession = async (session: Session) => {
    const therapist = therapists.find((t) => t.uid === session.therapistId);
    const reason = window.prompt(
      `Void session for ${therapist?.displayName || 'Unknown'}?\n` +
      `Table ${session.tableNo}, Seat ${session.seatNo}\n` +
      `Duration: ${session.totalMinutes}m, Received: ${session.amountReceived || 0}\n\n` +
      `Enter reason (required):`
    );
    if (!reason) return;

    setActionInProgress(`void-${session.id}`);
    try {
      const voidFn = httpsCallable(functions, 'adminVoidSession');
      await voidFn({ eventId: event.id, sessionId: session.id, reason });
      alert('Session voided successfully');
      await loadData(); // Refresh data
    } catch (err: any) {
      console.error('Error voiding session:', err);
      alert(err.message || 'Failed to void session');
    } finally {
      setActionInProgress(null);
    }
  };

  // Filter sessions based on selection
  const filteredSessions = React.useMemo(() => {
    let filtered = sessions;

    if (selectedDate) {
      filtered = filtered.filter((s) => {
        const sessionDate = s.completedAt?.toDate().toISOString().split('T')[0];
        return sessionDate === selectedDate;
      });
    }

    if (selectedTherapist) {
      filtered = filtered.filter((s) => s.therapistId === selectedTherapist);
    }

    return filtered;
  }, [sessions, selectedDate, selectedTherapist]);

  // Calculate totals for filtered sessions
  const totals = React.useMemo(() => {
    return filteredSessions.reduce(
      (acc, session) => {
        const newAcc = {
          sessions: acc.sessions + 1,
          minutes: acc.minutes + session.totalMinutes,
          bill: acc.bill + session.billAmount,
          received: acc.received + (session.amountReceived || 0),
          tips: acc.tips + (session.tipAmount || 0),
          therapistPayout: acc.therapistPayout + (session.therapistTotalPayout || 0),
          eventPayout: acc.eventPayout + (session.eventPayout || 0),
          // Rating stats
          ratingCount: acc.ratingCount + (session.customerRating ? 1 : 0),
          ratingSum: acc.ratingSum + (session.customerRating || 0),
          // Payment discrepancy stats
          discrepancyCount: acc.discrepancyCount + (session.paymentDiscrepancy !== undefined ? 1 : 0),
          discrepancyTotal: acc.discrepancyTotal + (session.paymentDiscrepancy || 0),
          significantDiscrepancies: acc.significantDiscrepancies + (Math.abs(session.paymentDiscrepancy || 0) >= 5 ? 1 : 0),
        };
        return newAcc;
      },
      {
        sessions: 0, minutes: 0, bill: 0, received: 0, tips: 0,
        therapistPayout: 0, eventPayout: 0,
        ratingCount: 0, ratingSum: 0,
        discrepancyCount: 0, discrepancyTotal: 0, significantDiscrepancies: 0
      }
    );
  }, [filteredSessions]);

  // Get sessions with significant payment discrepancies
  const discrepancySessions = React.useMemo(() => {
    return filteredSessions
      .filter((s) => s.paymentDiscrepancy !== undefined && Math.abs(s.paymentDiscrepancy) >= 5)
      .sort((a, b) => Math.abs(b.paymentDiscrepancy || 0) - Math.abs(a.paymentDiscrepancy || 0));
  }, [filteredSessions]);

  // Calculate daily reports
  const dailyReports: DailyReport[] = React.useMemo(() => {
    const reports: Record<string, DailyReport> = {};

    // Initialize all event days
    eventDays.forEach((date) => {
      const d = new Date(date);
      reports[date] = {
        date,
        displayDate: d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' }),
        sessionsCompleted: 0,
        minutesDelivered: 0,
        billTotal: 0,
        amountReceived: 0,
        tipsTotal: 0,
        eventPayout: 0,
      };
    });

    // Add sessions that might be outside the event dates
    sessions.forEach((session) => {
      const date = session.completedAt?.toDate().toISOString().split('T')[0];
      if (date && !reports[date]) {
        const d = new Date(date);
        reports[date] = {
          date,
          displayDate: d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' }),
          sessionsCompleted: 0,
          minutesDelivered: 0,
          billTotal: 0,
          amountReceived: 0,
          tipsTotal: 0,
          eventPayout: 0,
        };
      }
    });

    // Aggregate by day
    sessions.forEach((session) => {
      const date = session.completedAt?.toDate().toISOString().split('T')[0];
      if (date && reports[date]) {
        reports[date].sessionsCompleted += 1;
        reports[date].minutesDelivered += session.totalMinutes;
        reports[date].billTotal += session.billAmount;
        reports[date].amountReceived += session.amountReceived || 0;
        reports[date].tipsTotal += session.tipAmount || 0;
        reports[date].eventPayout += session.eventPayout || 0;
      }
    });

    return Object.values(reports).sort((a, b) => b.date.localeCompare(a.date));
  }, [sessions, eventDays]);

  // Calculate per-therapist reports (for filtered sessions)
  const therapistReports: TherapistReport[] = React.useMemo(() => {
    const reports: Record<string, TherapistReport> = {};

    // Build attendance totals per therapist
    const attendanceTotals: Record<string, { venueMinutes: number; breakMinutes: number }> = {};
    attendanceRecords.forEach((att) => {
      // If filtering by date, only include matching attendance
      if (selectedDate && att.date !== selectedDate) return;

      if (!attendanceTotals[att.therapistId]) {
        attendanceTotals[att.therapistId] = { venueMinutes: 0, breakMinutes: 0 };
      }
      attendanceTotals[att.therapistId].venueMinutes += att.venueMinutes || 0;
      attendanceTotals[att.therapistId].breakMinutes += att.totalBreakMinutes || 0;
    });

    therapists.forEach((t) => {
      const attData = attendanceTotals[t.uid] || { venueMinutes: 0, breakMinutes: 0 };
      const workMinutes = Math.max(0, attData.venueMinutes - attData.breakMinutes);
      reports[t.uid] = {
        uid: t.uid,
        displayName: t.displayName,
        thumbnailUrl: t.thumbnailUrl,
        photoUrl: t.photoUrl,
        sessionsCompleted: 0,
        minutesDelivered: 0,
        billTotal: 0,
        amountReceived: 0,
        tipsTotal: 0,
        therapistPayout: 0,
        ratingCount: 0,
        ratingSum: 0,
        avgRating: 0,
        venueMinutes: attData.venueMinutes,
        breakMinutes: attData.breakMinutes,
        workMinutes: workMinutes,
        utilizationPct: 0,
      };
    });

    filteredSessions.forEach((session) => {
      if (reports[session.therapistId]) {
        reports[session.therapistId].sessionsCompleted += 1;
        reports[session.therapistId].minutesDelivered += session.totalMinutes;
        reports[session.therapistId].billTotal += session.billAmount;
        reports[session.therapistId].amountReceived += session.amountReceived || 0;
        reports[session.therapistId].tipsTotal += session.tipAmount || 0;
        reports[session.therapistId].therapistPayout += session.therapistTotalPayout || 0;
        // Track ratings
        if (session.customerRating) {
          reports[session.therapistId].ratingCount += 1;
          reports[session.therapistId].ratingSum += session.customerRating;
        }
      }
    });

    // Calculate averages and utilization
    Object.values(reports).forEach((r) => {
      r.avgRating = r.ratingCount > 0 ? r.ratingSum / r.ratingCount : 0;
      // Utilization = session time / work time (venue - breaks)
      r.utilizationPct = r.workMinutes > 0 ? Math.round((r.minutesDelivered / r.workMinutes) * 100) : 0;
    });

    const filtered = Object.values(reports)
      .filter((r) => r.sessionsCompleted > 0 || !selectedDate); // Show all if no date filter, only active if filtered

    // Sort based on selected field
    return filtered.sort((a, b) => {
      let aVal: number | string = a[therapistSortField];
      let bVal: number | string = b[therapistSortField];

      // Handle string comparison for displayName
      if (therapistSortField === 'displayName') {
        return therapistSortDesc
          ? (bVal as string).localeCompare(aVal as string)
          : (aVal as string).localeCompare(bVal as string);
      }

      // Numeric comparison
      return therapistSortDesc ? (bVal as number) - (aVal as number) : (aVal as number) - (bVal as number);
    });
  }, [filteredSessions, therapists, selectedDate, therapistSortField, therapistSortDesc, attendanceRecords]);

  // Build grid data: days × masseuses with revenue
  const gridData = React.useMemo(() => {
    // Show ALL therapists, not just those with sessions
    const allTherapists = [...therapists].sort((a, b) => a.displayName.localeCompare(b.displayName));

    // Sort days chronologically (oldest first)
    const sortedDays = [...dailyReports].sort((a, b) => a.date.localeCompare(b.date));

    // Build revenue map: date -> therapistId -> revenue
    const revenueMap: Record<string, Record<string, number>> = {};

    sortedDays.forEach((day) => {
      revenueMap[day.date] = {};
      allTherapists.forEach((t) => {
        revenueMap[day.date][t.uid] = 0;
      });
    });

    sessions.forEach((session) => {
      const date = session.completedAt?.toDate().toISOString().split('T')[0];
      if (date && revenueMap[date]) {
        revenueMap[date][session.therapistId] =
          (revenueMap[date][session.therapistId] || 0) + (session.amountReceived || 0);
      }
    });

    // Calculate totals per therapist
    const therapistTotals: Record<string, number> = {};
    allTherapists.forEach((t) => {
      therapistTotals[t.uid] = 0;
    });
    sortedDays.forEach((day) => {
      allTherapists.forEach((t) => {
        therapistTotals[t.uid] += revenueMap[day.date][t.uid] || 0;
      });
    });

    // Calculate daily totals
    const dayTotals: Record<string, number> = {};
    sortedDays.forEach((day) => {
      dayTotals[day.date] = allTherapists.reduce(
        (sum, t) => sum + (revenueMap[day.date][t.uid] || 0),
        0
      );
    });

    // Grand total
    const grandTotal = Object.values(therapistTotals).reduce((sum, v) => sum + v, 0);

    return {
      therapists: allTherapists,
      days: sortedDays,
      revenueMap,
      therapistTotals,
      dayTotals,
      grandTotal,
    };
  }, [sessions, therapists, dailyReports]);

  // Calculate detailed report for selected therapist
  const selectedTherapistDetails = React.useMemo(() => {
    if (!selectedTherapist) return null;

    const therapist = therapists.find(t => t.uid === selectedTherapist);
    if (!therapist) return null;

    // Get all sessions for this therapist (ignoring date filter)
    const therapistSessions = sessions.filter(s => s.therapistId === selectedTherapist);

    // Get attendance records for this therapist
    const therapistAttendance = attendanceRecords.filter(a => a.therapistId === selectedTherapist);

    // Build a map of attendance by date
    const attendanceByDate: Record<string, TherapistAttendance> = {};
    therapistAttendance.forEach(a => {
      attendanceByDate[a.date] = a;
    });

    // Build daily breakdown with venue hours and break time
    const dailyBreakdown: Record<string, {
      date: string;
      displayDate: string;
      sessions: number;
      minutes: number;
      received: number;
      tips: number;
      payout: number;
      venueMinutes: number;
      breakMinutes: number;
      workMinutes: number;
      utilizationPct: number;
      hasAttendance: boolean;
      ratingCount: number;
      ratingSum: number;
    }> = {};

    // Initialize from attendance records (days they checked in)
    therapistAttendance.forEach(attendance => {
      const d = new Date(attendance.date);
      const breakMins = attendance.totalBreakMinutes || 0;
      const venueMins = attendance.venueMinutes || 0;
      dailyBreakdown[attendance.date] = {
        date: attendance.date,
        displayDate: d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' }),
        sessions: 0,
        minutes: 0,
        received: 0,
        tips: 0,
        payout: 0,
        venueMinutes: venueMins,
        breakMinutes: breakMins,
        workMinutes: Math.max(0, venueMins - breakMins),
        utilizationPct: 0,
        hasAttendance: true,
        ratingCount: 0,
        ratingSum: 0,
      };
    });

    // Calculate session times per day
    therapistSessions.forEach(session => {
      const date = session.completedAt?.toDate().toISOString().split('T')[0];
      if (date) {
        if (!dailyBreakdown[date]) {
          const d = new Date(date);
          dailyBreakdown[date] = {
            date,
            displayDate: d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' }),
            sessions: 0,
            minutes: 0,
            received: 0,
            tips: 0,
            payout: 0,
            venueMinutes: 0,
            breakMinutes: 0,
            workMinutes: 0,
            utilizationPct: 0,
            hasAttendance: false,
            ratingCount: 0,
            ratingSum: 0,
          };
        }
        dailyBreakdown[date].sessions += 1;
        dailyBreakdown[date].minutes += session.totalMinutes;
        dailyBreakdown[date].received += session.amountReceived || 0;
        dailyBreakdown[date].tips += session.tipAmount || 0;
        dailyBreakdown[date].payout += session.therapistTotalPayout || 0;
        // Track ratings
        if (session.customerRating) {
          dailyBreakdown[date].ratingCount += 1;
          dailyBreakdown[date].ratingSum += session.customerRating;
        }
      }
    });

    // For days without real attendance data, estimate venue time from session times
    const daySessionTimes: Record<string, { firstStart: Date | null; lastEnd: Date | null }> = {};
    therapistSessions.forEach(session => {
      const startDate = session.startedAt?.toDate();
      const endDate = session.completedAt?.toDate();
      if (!startDate || !endDate) return;

      const dateKey = startDate.toISOString().split('T')[0];
      if (!daySessionTimes[dateKey]) {
        daySessionTimes[dateKey] = { firstStart: null, lastEnd: null };
      }

      if (!daySessionTimes[dateKey].firstStart || startDate < daySessionTimes[dateKey].firstStart!) {
        daySessionTimes[dateKey].firstStart = startDate;
      }
      if (!daySessionTimes[dateKey].lastEnd || endDate > daySessionTimes[dateKey].lastEnd!) {
        daySessionTimes[dateKey].lastEnd = endDate;
      }
    });

    // Calculate venue time - use real attendance if available, otherwise estimate from sessions
    Object.keys(dailyBreakdown).forEach(date => {
      const day = dailyBreakdown[date];

      // If we have real attendance data, use it
      if (day.hasAttendance && day.venueMinutes > 0) {
        // venueMinutes and breakMinutes already set from attendance
        // workMinutes already calculated
      } else {
        // Estimate from session times
        const times = daySessionTimes[date];
        if (times?.firstStart && times?.lastEnd) {
          const venueMs = times.lastEnd.getTime() - times.firstStart.getTime();
          day.venueMinutes = Math.round(venueMs / 60000);
          // No break data available for estimated days
          day.workMinutes = day.venueMinutes;
        }
      }

      // Calculate utilization based on work time (venue - breaks)
      day.utilizationPct = day.workMinutes > 0
        ? Math.round((day.minutes / day.workMinutes) * 100)
        : 0;
    });

    const days = Object.values(dailyBreakdown).sort((a, b) => a.date.localeCompare(b.date));
    const daysWorked = days.length;
    const totalSessions = therapistSessions.length;
    const totalMinutes = therapistSessions.reduce((sum, s) => sum + s.totalMinutes, 0);
    const totalReceived = therapistSessions.reduce((sum, s) => sum + (s.amountReceived || 0), 0);
    const totalTips = therapistSessions.reduce((sum, s) => sum + (s.tipAmount || 0), 0);
    const totalPayout = therapistSessions.reduce((sum, s) => sum + (s.therapistTotalPayout || 0), 0);
    const avgSessionMinutes = totalSessions > 0 ? totalMinutes / totalSessions : 0;
    const avgMinutesPerDay = daysWorked > 0 ? totalMinutes / daysWorked : 0;
    const avgSessionsPerDay = daysWorked > 0 ? totalSessions / daysWorked : 0;
    const avgPayoutPerDay = daysWorked > 0 ? totalPayout / daysWorked : 0;

    // Calculate total venue time, break time, and overall utilization
    const totalVenueMinutes = days.reduce((sum, d) => sum + d.venueMinutes, 0);
    const totalBreakMinutes = days.reduce((sum, d) => sum + d.breakMinutes, 0);
    const totalWorkMinutes = days.reduce((sum, d) => sum + d.workMinutes, 0);
    // Utilization based on work time (venue - breaks)
    const overallUtilizationPct = totalWorkMinutes > 0
      ? Math.round((totalMinutes / totalWorkMinutes) * 100)
      : 0;

    // Check if we have real attendance data
    const hasRealAttendance = days.some(d => d.hasAttendance);

    // Calculate total ratings
    const totalRatingCount = days.reduce((sum, d) => sum + d.ratingCount, 0);
    const totalRatingSum = days.reduce((sum, d) => sum + d.ratingSum, 0);
    const avgRating = totalRatingCount > 0 ? totalRatingSum / totalRatingCount : 0;

    return {
      therapist,
      days,
      daysWorked,
      totalSessions,
      totalMinutes,
      totalReceived,
      totalTips,
      totalPayout,
      avgSessionMinutes,
      avgMinutesPerDay,
      avgSessionsPerDay,
      avgPayoutPerDay,
      totalVenueMinutes,
      totalBreakMinutes,
      totalWorkMinutes,
      overallUtilizationPct,
      hasRealAttendance,
      totalRatingCount,
      avgRating,
    };
  }, [selectedTherapist, sessions, therapists, attendanceRecords]);

  // Get currency symbol
  const getCurrencySymbol = () => {
    switch (event.currency) {
      case 'EUR': return '€';
      case 'USD': return '$';
      case 'GBP': return '£';
      case 'NOK': case 'SEK': case 'DKK': return 'kr';
      default: return event.currency;
    }
  };

  const currencySymbol = getCurrencySymbol();

  const formatCurrency = (amount: number) => {
    return amount.toFixed(0);
  };

  // Format minutes as hours and minutes (e.g., "1h 30m")
  const formatDuration = (minutes: number) => {
    const hours = Math.floor(minutes / 60);
    const mins = Math.round(minutes % 60);
    if (hours === 0) return `${mins}m`;
    if (mins === 0) return `${hours}h`;
    return `${hours}h ${mins}m`;
  };

  const exportCSV = () => {
    let headers: string[];
    let rows: (string | number)[][];
    let filename: string;

    if (viewMode === 'daily') {
      headers = ['Date', 'Sessions', 'Minutes', 'Bill Total', 'Received', 'Tips', 'Event Payout'];
      rows = dailyReports.map((r) => [
        r.date,
        r.sessionsCompleted,
        r.minutesDelivered,
        r.billTotal.toFixed(2),
        r.amountReceived.toFixed(2),
        r.tipsTotal.toFixed(2),
        r.eventPayout.toFixed(2),
      ]);
      filename = `${event.name}-daily-report.csv`;
    } else if (viewMode === 'grid') {
      // Grid export: Date, Total, then each masseuse
      headers = ['Date', 'Total', ...gridData.therapists.map((t) => t.displayName)];
      rows = gridData.days.map((day) => [
        day.date,
        gridData.dayTotals[day.date].toFixed(0),
        ...gridData.therapists.map((t) => (gridData.revenueMap[day.date][t.uid] || 0).toFixed(0)),
      ]);
      // Add totals row
      rows.push([
        'TOTAL',
        gridData.grandTotal.toFixed(0),
        ...gridData.therapists.map((t) => gridData.therapistTotals[t.uid].toFixed(0)),
      ]);
      filename = `${event.name}-festival-report.csv`;
    } else {
      headers = ['Therapist', 'Sessions', 'Minutes', 'Bill Total', 'Received', 'Tips', 'Payout'];
      rows = therapistReports.map((r) => [
        r.displayName,
        r.sessionsCompleted,
        r.minutesDelivered,
        r.billTotal.toFixed(2),
        r.amountReceived.toFixed(2),
        r.tipsTotal.toFixed(2),
        r.therapistPayout.toFixed(2),
      ]);
      filename = `${event.name}-therapist-report${selectedDate ? `-${selectedDate}` : ''}.csv`;
    }

    const csv = [headers, ...rows].map((row) => row.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
  };

  const clearFilters = () => {
    setSelectedDate(null);
    setSelectedTherapist(null);
  };

  // Handle therapist table sort
  const handleTherapistSort = (field: TherapistSortField) => {
    if (therapistSortField === field) {
      // Toggle direction if same field
      setTherapistSortDesc(!therapistSortDesc);
    } else {
      // New field, default to descending (highest first) except for name
      setTherapistSortField(field);
      setTherapistSortDesc(field !== 'displayName');
    }
  };

  // Sort indicator for headers
  const SortIndicator = ({ field }: { field: TherapistSortField }) => {
    if (therapistSortField !== field) return null;
    return <span style={{ marginLeft: '0.25rem' }}>{therapistSortDesc ? '▼' : '▲'}</span>;
  };

  const getFilterLabel = () => {
    const parts: string[] = [];
    if (selectedDate) {
      const d = new Date(selectedDate);
      parts.push(d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' }));
    }
    if (selectedTherapist) {
      const t = therapists.find((t) => t.uid === selectedTherapist);
      if (t) parts.push(t.displayName);
    }
    return parts.length > 0 ? parts.join(' - ') : 'All Time';
  };

  if (loading) {
    return <div className="loading">Loading reports...</div>;
  }

  return (
    <div className="event-reports">
      <div className="section-header">
        <h2>Reports</h2>
        <div className="header-actions" style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
          <button onClick={exportCSV} className="btn btn-secondary">
            Export CSV
          </button>
        </div>
      </div>

      {/* View Mode Tabs */}
      <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem', flexWrap: 'wrap' }}>
        <button
          onClick={() => { setViewMode('daily'); setSelectedTherapist(null); }}
          className={`btn ${viewMode === 'daily' ? 'btn-primary' : 'btn-secondary'}`}
        >
          By Day
        </button>
        <button
          onClick={() => { setViewMode('therapist'); setSelectedDate(null); }}
          className={`btn ${viewMode === 'therapist' ? 'btn-primary' : 'btn-secondary'}`}
        >
          By Therapist
        </button>
        <button
          onClick={() => { setViewMode('grid'); clearFilters(); }}
          className={`btn ${viewMode === 'grid' ? 'btn-primary' : 'btn-secondary'}`}
        >
          By Festival
        </button>
        <button
          onClick={() => { setViewMode('sessions'); clearFilters(); }}
          className={`btn ${viewMode === 'sessions' ? 'btn-primary' : 'btn-secondary'}`}
        >
          Session Log
        </button>
      </div>

      {/* Active Filter */}
      {(selectedDate || selectedTherapist) && (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '1rem',
          padding: '0.75rem 1rem',
          backgroundColor: 'var(--color-bg-secondary)',
          borderRadius: '8px',
          marginBottom: '1rem',
        }}>
          <span style={{ fontWeight: 600 }}>Showing: {getFilterLabel()}</span>
          <button onClick={clearFilters} className="btn btn-secondary" style={{ marginLeft: 'auto' }}>
            Clear Filter
          </button>
        </div>
      )}

      {/* Summary Stats */}
      <div className="report-summary">
        <div className="summary-card">
          <span className="label">Sessions</span>
          <span className="value">{totals.sessions}</span>
        </div>
        <div className="summary-card">
          <span className="label">Duration</span>
          <span className="value">{formatDuration(totals.minutes)}</span>
        </div>
        <div className="summary-card">
          <span className="label">Avg Session</span>
          <span className="value">{totals.sessions > 0 ? (totals.minutes / totals.sessions).toFixed(0) : 0} min</span>
        </div>
        <div className="summary-card">
          <span className="label">Received ({currencySymbol})</span>
          <span className="value">{formatCurrency(totals.received)}</span>
        </div>
        <div className="summary-card">
          <span className="label">Tips ({currencySymbol})</span>
          <span className="value">{formatCurrency(totals.tips)}</span>
        </div>
        <div className="summary-card highlight">
          <span className="label">Event Payout ({currencySymbol})</span>
          <span className="value">{formatCurrency(totals.eventPayout)}</span>
        </div>
      </div>

      {/* Customer Feedback Summary (if enabled) */}
      {(event.askCustomerRating || event.askCustomerAmount) && (totals.ratingCount > 0 || totals.discrepancyCount > 0) && (
        <div style={{
          marginTop: '1rem',
          padding: '1rem',
          backgroundColor: 'var(--color-bg-secondary)',
          borderRadius: '8px',
        }}>
          <h3 style={{ marginTop: 0, marginBottom: '1rem', fontSize: '1rem' }}>Customer Feedback</h3>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1.5rem' }}>
            {/* Rating Summary */}
            {event.askCustomerRating && totals.ratingCount > 0 && (
              <div>
                <div style={{ fontSize: '0.75rem', color: 'var(--color-text-secondary)', marginBottom: '0.25rem' }}>
                  Average Rating ({totals.ratingCount} reviews)
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <span style={{ fontSize: '1.5rem', fontWeight: 700, color: '#f59e0b' }}>
                    {(totals.ratingSum / totals.ratingCount).toFixed(1)}
                  </span>
                  <span style={{ fontSize: '1.25rem', color: '#f59e0b' }}>{'\u2605'}</span>
                </div>
              </div>
            )}
            {/* Payment Discrepancy Summary */}
            {event.askCustomerAmount && totals.discrepancyCount > 0 && (
              <div>
                <div style={{ fontSize: '0.75rem', color: 'var(--color-text-secondary)', marginBottom: '0.25rem' }}>
                  Payment Verification ({totals.discrepancyCount} responses)
                </div>
                {totals.significantDiscrepancies > 0 ? (
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    padding: '0.5rem 0.75rem',
                    backgroundColor: 'rgba(239, 68, 68, 0.1)',
                    borderRadius: '6px',
                    border: '1px solid #ef4444',
                  }}>
                    <span style={{ color: '#ef4444', fontWeight: 600 }}>
                      {totals.significantDiscrepancies} discrepancies
                    </span>
                    <span style={{ fontSize: '0.875rem', color: 'var(--color-text-secondary)' }}>
                      ({'\u2265'}{currencySymbol}5 difference)
                    </span>
                  </div>
                ) : (
                  <div style={{ color: 'var(--color-green)', fontWeight: 600 }}>
                    {'\u2713'} All payments verified
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Payment Discrepancy Details */}
      {event.askCustomerAmount && discrepancySessions.length > 0 && (
        <div style={{
          marginTop: '1rem',
          padding: '1rem',
          backgroundColor: 'rgba(239, 68, 68, 0.05)',
          borderRadius: '8px',
          border: '1px solid rgba(239, 68, 68, 0.2)',
        }}>
          <h3 style={{ marginTop: 0, marginBottom: '0.5rem', fontSize: '1rem', color: '#ef4444' }}>
            Payment Discrepancies
          </h3>
          <p style={{ fontSize: '0.875rem', color: 'var(--color-text-secondary)', marginBottom: '1rem' }}>
            Sessions where customer-reported amount differs from therapist-reported amount by {'\u2265'}{currencySymbol}5
          </p>
          <table className="report-table" style={{ fontSize: '0.875rem' }}>
            <thead>
              <tr>
                <th>Time</th>
                <th>Therapist</th>
                <th>Location</th>
                <th style={{ textAlign: 'right' }}>Therapist Said</th>
                <th style={{ textAlign: 'right' }}>Customer Said</th>
                <th style={{ textAlign: 'right' }}>Difference</th>
              </tr>
            </thead>
            <tbody>
              {discrepancySessions.slice(0, 10).map((session) => {
                const therapist = therapists.find((t) => t.uid === session.therapistId);
                const diff = session.paymentDiscrepancy || 0;
                return (
                  <tr key={session.id}>
                    <td>
                      {session.completedAt?.toDate().toLocaleString('en-US', {
                        month: 'short',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit',
                      })}
                    </td>
                    <td>{therapist?.displayName || 'Unknown'}</td>
                    <td>T{session.tableNo} S{session.seatNo}</td>
                    <td style={{ textAlign: 'right' }}>{currencySymbol}{session.amountReceived || 0}</td>
                    <td style={{ textAlign: 'right' }}>{currencySymbol}{session.customerPaidAmount || 0}</td>
                    <td style={{
                      textAlign: 'right',
                      fontWeight: 600,
                      color: diff > 0 ? 'var(--color-green)' : '#ef4444',
                    }}>
                      {diff > 0 ? '+' : ''}{currencySymbol}{diff}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {discrepancySessions.length > 10 && (
            <p style={{ marginTop: '0.5rem', fontSize: '0.875rem', color: 'var(--color-text-secondary)' }}>
              Showing 10 of {discrepancySessions.length} discrepancies
            </p>
          )}
        </div>
      )}

      {/* Daily View */}
      {viewMode === 'daily' && (
        <section className="report-section">
          <h3>Daily Summary</h3>
          <p style={{ fontSize: '0.875rem', color: 'var(--color-text-secondary)', marginBottom: '1rem' }}>
            Click a day to see details
          </p>
          <table className="report-table">
            <thead>
              <tr>
                <th>Date</th>
                <th style={{ textAlign: 'right' }}>Sessions</th>
                <th style={{ textAlign: 'right' }}>Duration</th>
                <th style={{ textAlign: 'right' }}>Avg</th>
                <th style={{ textAlign: 'right' }}>Bill ({currencySymbol})</th>
                <th style={{ textAlign: 'right' }}>Received ({currencySymbol})</th>
                <th style={{ textAlign: 'right' }}>Tips ({currencySymbol})</th>
                <th style={{ textAlign: 'right' }}>Event Payout ({currencySymbol})</th>
              </tr>
            </thead>
            <tbody>
              {dailyReports.map((report) => (
                <tr
                  key={report.date}
                  onClick={() => setSelectedDate(selectedDate === report.date ? null : report.date)}
                  style={{
                    cursor: 'pointer',
                    backgroundColor: selectedDate === report.date ? 'var(--color-bg-secondary)' : undefined,
                  }}
                >
                  <td style={{ fontWeight: 500 }}>{report.displayDate}</td>
                  <td style={{ textAlign: 'right' }}>{report.sessionsCompleted}</td>
                  <td style={{ textAlign: 'right' }}>{formatDuration(report.minutesDelivered)}</td>
                  <td style={{ textAlign: 'right' }}>{report.sessionsCompleted > 0 ? formatDuration(report.minutesDelivered / report.sessionsCompleted) : '-'}</td>
                  <td style={{ textAlign: 'right' }}>{formatCurrency(report.billTotal)}</td>
                  <td style={{ textAlign: 'right' }}>{formatCurrency(report.amountReceived)}</td>
                  <td style={{ textAlign: 'right' }}>{formatCurrency(report.tipsTotal)}</td>
                  <td className="highlight" style={{ textAlign: 'right' }}>{formatCurrency(report.eventPayout)}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr style={{
                backgroundColor: 'var(--color-bg-secondary)',
                borderTop: '2px solid var(--color-border)',
              }}>
                <td style={{ padding: '0.75rem 0.5rem' }}>Total</td>
                <td style={{ padding: '0.75rem 0.5rem', textAlign: 'right' }}>{totals.sessions}</td>
                <td style={{ padding: '0.75rem 0.5rem', textAlign: 'right' }}>{formatDuration(totals.minutes)}</td>
                <td style={{ padding: '0.75rem 0.5rem', textAlign: 'right' }}>{totals.sessions > 0 ? formatDuration(totals.minutes / totals.sessions) : '-'}</td>
                <td style={{ padding: '0.75rem 0.5rem', textAlign: 'right' }}>{formatCurrency(totals.bill)}</td>
                <td style={{ padding: '0.75rem 0.5rem', textAlign: 'right' }}>{formatCurrency(totals.received)}</td>
                <td style={{ padding: '0.75rem 0.5rem', textAlign: 'right' }}>{formatCurrency(totals.tips)}</td>
                <td style={{ padding: '0.75rem 0.5rem', textAlign: 'right', color: 'var(--color-green)' }}>{formatCurrency(totals.eventPayout)}</td>
              </tr>
            </tfoot>
          </table>
        </section>
      )}

      {/* Therapist View */}
      {viewMode === 'therapist' && (
        <section className="report-section">
          <h3>By Therapist {selectedDate && `(${new Date(selectedDate).toLocaleDateString()})`}</h3>
          <p style={{ fontSize: '0.875rem', color: 'var(--color-text-secondary)', marginBottom: '1rem' }}>
            Click a therapist to see their sessions
          </p>
          <table className="report-table">
            <thead>
              <tr>
                <th style={{ width: '50px' }}></th>
                <th
                  style={{ cursor: 'pointer' }}
                  onClick={() => handleTherapistSort('displayName')}
                >
                  Therapist<SortIndicator field="displayName" />
                </th>
                <th
                  style={{ textAlign: 'right', cursor: 'pointer' }}
                  onClick={() => handleTherapistSort('sessionsCompleted')}
                >
                  Sessions<SortIndicator field="sessionsCompleted" />
                </th>
                <th
                  style={{ textAlign: 'right', cursor: 'pointer' }}
                  onClick={() => handleTherapistSort('minutesDelivered')}
                >
                  Duration<SortIndicator field="minutesDelivered" />
                </th>
                <th style={{ textAlign: 'right' }}>Avg</th>
                <th
                  style={{ textAlign: 'right', cursor: 'pointer' }}
                  onClick={() => handleTherapistSort('billTotal')}
                >
                  Bill ({currencySymbol})<SortIndicator field="billTotal" />
                </th>
                <th
                  style={{ textAlign: 'right', cursor: 'pointer' }}
                  onClick={() => handleTherapistSort('amountReceived')}
                >
                  Received ({currencySymbol})<SortIndicator field="amountReceived" />
                </th>
                <th
                  style={{ textAlign: 'right', cursor: 'pointer' }}
                  onClick={() => handleTherapistSort('tipsTotal')}
                >
                  Tips ({currencySymbol})<SortIndicator field="tipsTotal" />
                </th>
                <th
                  style={{ textAlign: 'right', cursor: 'pointer' }}
                  onClick={() => handleTherapistSort('therapistPayout')}
                >
                  Payout ({currencySymbol})<SortIndicator field="therapistPayout" />
                </th>
                <th
                  style={{ textAlign: 'right', cursor: 'pointer' }}
                  onClick={() => handleTherapistSort('breakMinutes')}
                >
                  Break<SortIndicator field="breakMinutes" />
                </th>
                <th
                  style={{ textAlign: 'right', cursor: 'pointer' }}
                  onClick={() => handleTherapistSort('utilizationPct')}
                >
                  Util %<SortIndicator field="utilizationPct" />
                </th>
                {event.askCustomerRating && (
                  <th
                    style={{ textAlign: 'center', cursor: 'pointer' }}
                    onClick={() => handleTherapistSort('avgRating')}
                  >
                    Rating<SortIndicator field="avgRating" />
                  </th>
                )}
              </tr>
            </thead>
            <tbody>
              {therapistReports.map((report) => (
                <tr
                  key={report.uid}
                  onClick={() => setSelectedTherapist(selectedTherapist === report.uid ? null : report.uid)}
                  style={{
                    cursor: 'pointer',
                    backgroundColor: selectedTherapist === report.uid ? 'var(--color-bg-secondary)' : undefined,
                  }}
                >
                  <td>
                    {(report.thumbnailUrl || report.photoUrl) ? (
                      <img
                        src={report.thumbnailUrl || report.photoUrl}
                        alt=""
                        style={{
                          width: '36px',
                          height: '36px',
                          borderRadius: '50%',
                          objectFit: 'cover',
                        }}
                      />
                    ) : (
                      <div style={{
                        width: '36px',
                        height: '36px',
                        borderRadius: '50%',
                        backgroundColor: 'var(--color-bg-tertiary)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '0.875rem',
                        fontWeight: 600,
                      }}>
                        {report.displayName.charAt(0)}
                      </div>
                    )}
                  </td>
                  <td>{report.displayName}</td>
                  <td style={{ textAlign: 'right' }}>{report.sessionsCompleted}</td>
                  <td style={{ textAlign: 'right' }}>{formatDuration(report.minutesDelivered)}</td>
                  <td style={{ textAlign: 'right' }}>{report.sessionsCompleted > 0 ? formatDuration(report.minutesDelivered / report.sessionsCompleted) : '-'}</td>
                  <td style={{ textAlign: 'right' }}>{formatCurrency(report.billTotal)}</td>
                  <td style={{ textAlign: 'right' }}>{formatCurrency(report.amountReceived)}</td>
                  <td style={{ textAlign: 'right' }}>{formatCurrency(report.tipsTotal)}</td>
                  <td className="highlight" style={{ textAlign: 'right' }}>{formatCurrency(report.therapistPayout)}</td>
                  <td style={{ textAlign: 'right', color: report.breakMinutes > 0 ? '#3b82f6' : 'var(--color-text-tertiary)' }}>
                    {report.breakMinutes > 0 ? formatDuration(report.breakMinutes) : '-'}
                  </td>
                  <td style={{ textAlign: 'right', color: report.utilizationPct >= 50 ? 'var(--color-green)' : undefined }}>
                    {report.workMinutes > 0 ? `${report.utilizationPct}%` : '-'}
                  </td>
                  {event.askCustomerRating && (
                    <td style={{ textAlign: 'center' }}>
                      {report.ratingCount > 0 ? (
                        <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.25rem' }}>
                          <span style={{ color: '#f59e0b' }}>{report.avgRating.toFixed(1)}</span>
                          <span style={{ color: '#f59e0b', fontSize: '0.875rem' }}>{'\u2605'}</span>
                          <span style={{ color: 'var(--color-text-secondary)', fontSize: '0.75rem' }}>({report.ratingCount})</span>
                        </span>
                      ) : (
                        <span style={{ color: 'var(--color-text-tertiary)' }}>-</span>
                      )}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr style={{
                backgroundColor: 'var(--color-bg-secondary)',
                borderTop: '2px solid var(--color-border)',
              }}>
                <td style={{ padding: '0.75rem 0.5rem' }}></td>
                <td style={{ padding: '0.75rem 0.5rem' }}>Total</td>
                <td style={{ padding: '0.75rem 0.5rem', textAlign: 'right' }}>{totals.sessions}</td>
                <td style={{ padding: '0.75rem 0.5rem', textAlign: 'right' }}>{formatDuration(totals.minutes)}</td>
                <td style={{ padding: '0.75rem 0.5rem', textAlign: 'right' }}>{totals.sessions > 0 ? formatDuration(totals.minutes / totals.sessions) : '-'}</td>
                <td style={{ padding: '0.75rem 0.5rem', textAlign: 'right' }}>{formatCurrency(totals.bill)}</td>
                <td style={{ padding: '0.75rem 0.5rem', textAlign: 'right' }}>{formatCurrency(totals.received)}</td>
                <td style={{ padding: '0.75rem 0.5rem', textAlign: 'right' }}>{formatCurrency(totals.tips)}</td>
                <td style={{ padding: '0.75rem 0.5rem', textAlign: 'right', color: 'var(--color-green)' }}>{formatCurrency(totals.therapistPayout)}</td>
                <td style={{ padding: '0.75rem 0.5rem', textAlign: 'right', color: '#3b82f6' }}>
                  {formatDuration(therapistReports.reduce((sum, r) => sum + r.breakMinutes, 0))}
                </td>
                <td style={{ padding: '0.75rem 0.5rem', textAlign: 'right' }}>-</td>
                {event.askCustomerRating && (
                  <td style={{ padding: '0.75rem 0.5rem', textAlign: 'center' }}>
                    {totals.ratingCount > 0 ? (
                      <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.25rem' }}>
                        <span style={{ color: '#f59e0b' }}>{(totals.ratingSum / totals.ratingCount).toFixed(1)}</span>
                        <span style={{ color: '#f59e0b', fontSize: '0.875rem' }}>{'\u2605'}</span>
                      </span>
                    ) : '-'}
                  </td>
                )}
              </tr>
            </tfoot>
          </table>

          {/* Detailed Therapist Report */}
          {selectedTherapistDetails && (
            <div style={{
              marginTop: '2rem',
              padding: '1.5rem',
              backgroundColor: 'var(--color-bg-secondary)',
              borderRadius: '12px',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1.5rem' }}>
                {(selectedTherapistDetails.therapist.thumbnailUrl || selectedTherapistDetails.therapist.photoUrl) ? (
                  <img
                    src={selectedTherapistDetails.therapist.thumbnailUrl || selectedTherapistDetails.therapist.photoUrl}
                    alt=""
                    style={{
                      width: '60px',
                      height: '60px',
                      borderRadius: '50%',
                      objectFit: 'cover',
                    }}
                  />
                ) : (
                  <div style={{
                    width: '60px',
                    height: '60px',
                    borderRadius: '50%',
                    backgroundColor: 'var(--color-bg-tertiary)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '1.5rem',
                    fontWeight: 600,
                  }}>
                    {selectedTherapistDetails.therapist.displayName.charAt(0)}
                  </div>
                )}
                <div>
                  <h3 style={{ margin: 0 }}>{selectedTherapistDetails.therapist.displayName}</h3>
                  <p style={{ margin: '0.25rem 0 0', color: 'var(--color-text-secondary)', fontSize: '0.875rem' }}>
                    Detailed Report
                  </p>
                </div>
              </div>

              {/* Summary Stats */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
                gap: '1rem',
                marginBottom: '1.5rem',
              }}>
                <div style={{ textAlign: 'center', padding: '0.75rem', backgroundColor: 'var(--color-bg-primary)', borderRadius: '8px' }}>
                  <div style={{ fontSize: '0.75rem', color: 'var(--color-text-secondary)', marginBottom: '0.25rem' }}>Days Worked</div>
                  <div style={{ fontSize: '1.25rem', fontWeight: 700 }}>{selectedTherapistDetails.daysWorked}</div>
                </div>
                <div style={{ textAlign: 'center', padding: '0.75rem', backgroundColor: 'var(--color-bg-primary)', borderRadius: '8px' }}>
                  <div style={{ fontSize: '0.75rem', color: 'var(--color-text-secondary)', marginBottom: '0.25rem' }}>Total Sessions</div>
                  <div style={{ fontSize: '1.25rem', fontWeight: 700 }}>{selectedTherapistDetails.totalSessions}</div>
                </div>
                <div style={{ textAlign: 'center', padding: '0.75rem', backgroundColor: 'var(--color-bg-primary)', borderRadius: '8px' }}>
                  <div style={{ fontSize: '0.75rem', color: 'var(--color-text-secondary)', marginBottom: '0.25rem' }}>Session Time</div>
                  <div style={{ fontSize: '1.25rem', fontWeight: 700 }}>{formatDuration(selectedTherapistDetails.totalMinutes)}</div>
                </div>
                <div style={{ textAlign: 'center', padding: '0.75rem', backgroundColor: 'var(--color-bg-primary)', borderRadius: '8px' }}>
                  <div style={{ fontSize: '0.75rem', color: 'var(--color-text-secondary)', marginBottom: '0.25rem' }}>
                    Venue Time {!selectedTherapistDetails.hasRealAttendance && <span title="Estimated from session times">*</span>}
                  </div>
                  <div style={{ fontSize: '1.25rem', fontWeight: 700 }}>{formatDuration(selectedTherapistDetails.totalVenueMinutes)}</div>
                </div>
                <div style={{ textAlign: 'center', padding: '0.75rem', backgroundColor: selectedTherapistDetails.totalBreakMinutes > 0 ? 'rgba(59, 130, 246, 0.1)' : 'var(--color-bg-primary)', borderRadius: '8px', border: selectedTherapistDetails.totalBreakMinutes > 0 ? '1px solid #3b82f6' : undefined }}>
                  <div style={{ fontSize: '0.75rem', color: 'var(--color-text-secondary)', marginBottom: '0.25rem' }}>Break Time</div>
                  <div style={{ fontSize: '1.25rem', fontWeight: 700, color: selectedTherapistDetails.totalBreakMinutes > 0 ? '#3b82f6' : undefined }}>{formatDuration(selectedTherapistDetails.totalBreakMinutes)}</div>
                </div>
                <div style={{ textAlign: 'center', padding: '0.75rem', backgroundColor: selectedTherapistDetails.overallUtilizationPct >= 50 ? 'rgba(34, 197, 94, 0.15)' : 'var(--color-bg-primary)', borderRadius: '8px', border: selectedTherapistDetails.overallUtilizationPct >= 50 ? '1px solid var(--color-green)' : undefined }}>
                  <div style={{ fontSize: '0.75rem', color: 'var(--color-text-secondary)', marginBottom: '0.25rem' }}>Utilization</div>
                  <div style={{ fontSize: '1.25rem', fontWeight: 700, color: selectedTherapistDetails.overallUtilizationPct >= 50 ? 'var(--color-green)' : undefined }}>{selectedTherapistDetails.overallUtilizationPct}%</div>
                </div>
                <div style={{ textAlign: 'center', padding: '0.75rem', backgroundColor: 'var(--color-bg-primary)', borderRadius: '8px' }}>
                  <div style={{ fontSize: '0.75rem', color: 'var(--color-text-secondary)', marginBottom: '0.25rem' }}>Avg Session</div>
                  <div style={{ fontSize: '1.25rem', fontWeight: 700 }}>{formatDuration(selectedTherapistDetails.avgSessionMinutes)}</div>
                </div>
                <div style={{ textAlign: 'center', padding: '0.75rem', backgroundColor: 'var(--color-bg-primary)', borderRadius: '8px' }}>
                  <div style={{ fontSize: '0.75rem', color: 'var(--color-text-secondary)', marginBottom: '0.25rem' }}>Sessions/Day</div>
                  <div style={{ fontSize: '1.25rem', fontWeight: 700 }}>{selectedTherapistDetails.avgSessionsPerDay.toFixed(1)}</div>
                </div>
                <div style={{ textAlign: 'center', padding: '0.75rem', backgroundColor: 'var(--color-bg-primary)', borderRadius: '8px' }}>
                  <div style={{ fontSize: '0.75rem', color: 'var(--color-text-secondary)', marginBottom: '0.25rem' }}>Total Payout ({currencySymbol})</div>
                  <div style={{ fontSize: '1.25rem', fontWeight: 700, color: 'var(--color-green)' }}>{formatCurrency(selectedTherapistDetails.totalPayout)}</div>
                </div>
                <div style={{ textAlign: 'center', padding: '0.75rem', backgroundColor: 'var(--color-bg-primary)', borderRadius: '8px' }}>
                  <div style={{ fontSize: '0.75rem', color: 'var(--color-text-secondary)', marginBottom: '0.25rem' }}>Payout/Day ({currencySymbol})</div>
                  <div style={{ fontSize: '1.25rem', fontWeight: 700, color: 'var(--color-green)' }}>{formatCurrency(selectedTherapistDetails.avgPayoutPerDay)}</div>
                </div>
              </div>

              {/* Note about attendance data */}
              {!selectedTherapistDetails.hasRealAttendance && (
                <div style={{
                  padding: '0.75rem',
                  backgroundColor: 'rgba(249, 115, 22, 0.1)',
                  borderRadius: '8px',
                  marginBottom: '1rem',
                  fontSize: '0.875rem',
                  color: 'var(--color-text-secondary)',
                }}>
                  * Venue hours are estimated from session times. For accurate tracking, therapists should check in/out daily.
                </div>
              )}

              {/* Daily Breakdown */}
              <h4 style={{ marginBottom: '0.75rem' }}>Daily Breakdown</h4>
              <table className="report-table" style={{ fontSize: '0.875rem' }}>
                <thead>
                  <tr>
                    <th>Date</th>
                    <th style={{ textAlign: 'right' }}>Sessions</th>
                    <th style={{ textAlign: 'right' }}>Session Time</th>
                    <th style={{ textAlign: 'right' }}>Venue Time</th>
                    <th style={{ textAlign: 'right' }}>Break</th>
                    <th style={{ textAlign: 'right' }}>Util %</th>
                    <th style={{ textAlign: 'right' }}>Avg</th>
                    <th style={{ textAlign: 'right' }}>Payout ({currencySymbol})</th>
                    {event.askCustomerRating && <th style={{ textAlign: 'center' }}>Rating</th>}
                  </tr>
                </thead>
                <tbody>
                  {selectedTherapistDetails.days.map((day) => (
                    <tr key={day.date}>
                      <td style={{ padding: '0.5rem', fontWeight: 500 }}>{day.displayDate}</td>
                      <td style={{ padding: '0.5rem', textAlign: 'right' }}>{day.sessions}</td>
                      <td style={{ padding: '0.5rem', textAlign: 'right' }}>{formatDuration(day.minutes)}</td>
                      <td style={{ padding: '0.5rem', textAlign: 'right' }}>{day.venueMinutes > 0 ? formatDuration(day.venueMinutes) : '-'}</td>
                      <td style={{ padding: '0.5rem', textAlign: 'right', color: day.breakMinutes > 0 ? '#3b82f6' : 'var(--color-text-tertiary)' }}>{day.breakMinutes > 0 ? formatDuration(day.breakMinutes) : '-'}</td>
                      <td style={{ padding: '0.5rem', textAlign: 'right', color: day.utilizationPct >= 50 ? 'var(--color-green)' : undefined }}>{day.utilizationPct > 0 ? day.utilizationPct + '%' : '-'}</td>
                      <td style={{ padding: '0.5rem', textAlign: 'right' }}>{day.sessions > 0 ? formatDuration(day.minutes / day.sessions) : '-'}</td>
                      <td style={{ padding: '0.5rem', textAlign: 'right', color: 'var(--color-green)' }}>{formatCurrency(day.payout)}</td>
                      {event.askCustomerRating && (
                        <td style={{ padding: '0.5rem', textAlign: 'center' }}>
                          {day.ratingCount > 0 ? (
                            <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '0.25rem' }}>
                              <span style={{ color: '#f59e0b' }}>{(day.ratingSum / day.ratingCount).toFixed(1)}</span>
                              <span style={{ color: '#f59e0b', fontSize: '0.75rem' }}>{'\u2605'}</span>
                              <span style={{ color: 'var(--color-text-secondary)', fontSize: '0.7rem' }}>({day.ratingCount})</span>
                            </span>
                          ) : '-'}
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr style={{
                    backgroundColor: 'var(--color-bg-primary)',
                    borderTop: '2px solid var(--color-border)',
                  }}>
                    <td style={{ padding: '0.5rem' }}>Total</td>
                    <td style={{ padding: '0.5rem', textAlign: 'right' }}>{selectedTherapistDetails.totalSessions}</td>
                    <td style={{ padding: '0.5rem', textAlign: 'right' }}>{formatDuration(selectedTherapistDetails.totalMinutes)}</td>
                    <td style={{ padding: '0.5rem', textAlign: 'right' }}>{formatDuration(selectedTherapistDetails.totalVenueMinutes)}</td>
                    <td style={{ padding: '0.5rem', textAlign: 'right', color: '#3b82f6' }}>{formatDuration(selectedTherapistDetails.totalBreakMinutes)}</td>
                    <td style={{ padding: '0.5rem', textAlign: 'right', color: selectedTherapistDetails.overallUtilizationPct >= 50 ? 'var(--color-green)' : undefined }}>{selectedTherapistDetails.overallUtilizationPct}%</td>
                    <td style={{ padding: '0.5rem', textAlign: 'right' }}>{formatDuration(selectedTherapistDetails.avgSessionMinutes)}</td>
                    <td style={{ padding: '0.5rem', textAlign: 'right', color: 'var(--color-green)' }}>{formatCurrency(selectedTherapistDetails.totalPayout)}</td>
                    {event.askCustomerRating && (
                      <td style={{ padding: '0.5rem', textAlign: 'center' }}>
                        {selectedTherapistDetails.totalRatingCount > 0 ? (
                          <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '0.25rem' }}>
                            <span style={{ color: '#f59e0b' }}>{selectedTherapistDetails.avgRating.toFixed(1)}</span>
                            <span style={{ color: '#f59e0b', fontSize: '0.75rem' }}>{'\u2605'}</span>
                          </span>
                        ) : '-'}
                      </td>
                    )}
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </section>
      )}

      {/* Festival View - Days × Therapists */}
      {viewMode === 'grid' && (
        <section className="report-section">
          <h3>Festival Revenue</h3>
          <div style={{ overflowX: 'auto' }}>
            <table className="report-table" style={{ width: 'auto', tableLayout: 'auto' }}>
              <thead>
                <tr>
                  <th style={{ minWidth: '100px', whiteSpace: 'nowrap' }}>
                    Date
                  </th>
                  <th style={{ fontWeight: 700, textAlign: 'right', minWidth: '80px' }}>Total</th>
                  {gridData.therapists.map((t) => (
                    <th key={t.uid} style={{ textAlign: 'center', minWidth: '80px', padding: '0.5rem' }}>
                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px' }}>
                        {(t.thumbnailUrl || t.photoUrl) ? (
                          <img
                            src={t.thumbnailUrl || t.photoUrl}
                            alt=""
                            style={{
                              width: '40px',
                              height: '40px',
                              borderRadius: '50%',
                              objectFit: 'cover',
                            }}
                          />
                        ) : (
                          <div style={{
                            width: '40px',
                            height: '40px',
                            borderRadius: '50%',
                            backgroundColor: 'var(--color-bg-tertiary)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '0.875rem',
                            fontWeight: 600,
                          }}>
                            {t.displayName.charAt(0)}
                          </div>
                        )}
                        <span style={{ fontSize: '0.7rem', maxWidth: '70px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {t.displayName}
                        </span>
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {gridData.days.map((day) => (
                  <tr key={day.date}>
                    <td style={{ fontWeight: 500, whiteSpace: 'nowrap' }}>
                      {day.displayDate}
                    </td>
                    <td style={{ fontWeight: 600, textAlign: 'right' }}>
                      {formatCurrency(gridData.dayTotals[day.date])}
                    </td>
                    {gridData.therapists.map((t) => {
                      const revenue = gridData.revenueMap[day.date][t.uid] || 0;
                      return (
                        <td key={t.uid} style={{ textAlign: 'right', color: revenue > 0 ? undefined : 'var(--color-text-tertiary)' }}>
                          {revenue > 0 ? formatCurrency(revenue) : '-'}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr style={{
                  backgroundColor: 'var(--color-bg-secondary)',
                  borderTop: '2px solid var(--color-border)',
                }}>
                  <td style={{ padding: '0.75rem 0.5rem' }}>
                    Total
                  </td>
                  <td style={{ padding: '0.75rem 0.5rem', textAlign: 'right', color: 'var(--color-green)' }}>
                    {formatCurrency(gridData.grandTotal)}
                  </td>
                  {gridData.therapists.map((t) => (
                    <td key={t.uid} style={{ padding: '0.75rem 0.5rem', textAlign: 'right' }}>
                      {formatCurrency(gridData.therapistTotals[t.uid])}
                    </td>
                  ))}
                </tr>
              </tfoot>
            </table>
          </div>
        </section>
      )}

      {/* Session Log View */}
      {viewMode === 'sessions' && (
        <section className="report-section">
          <h3>Session Log</h3>
          <p style={{ fontSize: '0.875rem', color: 'var(--color-text-secondary)', marginBottom: '1rem' }}>
            All sessions, newest first. Showing {allSessions.length} sessions.
          </p>
          <table className="report-table">
            <thead>
              <tr>
                <th>Time</th>
                <th style={{ width: '50px' }}></th>
                <th>Therapist</th>
                <th style={{ textAlign: 'right' }}>Table</th>
                <th style={{ textAlign: 'right' }}>Seat</th>
                <th style={{ textAlign: 'right' }}>Duration</th>
                <th style={{ textAlign: 'center' }}>Status</th>
                <th style={{ textAlign: 'right' }}>Bill ({currencySymbol})</th>
                <th style={{ textAlign: 'right' }}>Received ({currencySymbol})</th>
                <th style={{ textAlign: 'right' }}>Tip ({currencySymbol})</th>
                {event.askCustomerAmount && <th style={{ textAlign: 'right' }}>Cust. Paid ({currencySymbol})</th>}
                {event.askCustomerRating && <th style={{ textAlign: 'center' }}>Rating</th>}
                {isSuperAdmin && <th style={{ textAlign: 'center' }}>Actions</th>}
              </tr>
            </thead>
            <tbody>
              {allSessions.map((session) => {
                const therapist = therapists.find((t) => t.uid === session.therapistId);
                const isActive = session.status === 'active';
                const displayTime = isActive
                  ? session.startedAt?.toDate()
                  : session.completedAt?.toDate();
                return (
                  <tr
                    key={session.id}
                    style={{
                      backgroundColor: isActive ? 'rgba(239, 68, 68, 0.1)' : undefined,
                    }}
                  >
                    <td>
                      {displayTime?.toLocaleString('en-US', {
                        month: 'short',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit',
                      }) || '-'}
                    </td>
                    <td>
                      {(therapist?.thumbnailUrl || therapist?.photoUrl) ? (
                        <img
                          src={therapist.thumbnailUrl || therapist.photoUrl}
                          alt=""
                          style={{
                            width: '32px',
                            height: '32px',
                            borderRadius: '50%',
                            objectFit: 'cover',
                          }}
                        />
                      ) : (
                        <div style={{
                          width: '32px',
                          height: '32px',
                          borderRadius: '50%',
                          backgroundColor: 'var(--color-bg-tertiary)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '0.75rem',
                          fontWeight: 600,
                        }}>
                          {therapist?.displayName?.charAt(0) || '?'}
                        </div>
                      )}
                    </td>
                    <td>{therapist?.displayName || 'Unknown'}</td>
                    <td style={{ textAlign: 'right' }}>{session.tableNo}</td>
                    <td style={{ textAlign: 'right' }}>{session.seatNo}</td>
                    <td style={{ textAlign: 'right' }}>
                      {isActive ? (
                        <span style={{ color: 'var(--color-text-secondary)' }}>
                          {session.totalMinutes}m
                        </span>
                      ) : (
                        formatDuration(session.actualMinutes ?? session.totalMinutes)
                      )}
                    </td>
                    <td style={{ textAlign: 'center' }}>
                      {isActive ? (
                        <span style={{
                          display: 'inline-block',
                          padding: '0.25rem 0.5rem',
                          borderRadius: '4px',
                          backgroundColor: 'rgba(239, 68, 68, 0.2)',
                          color: '#ef4444',
                          fontSize: '0.75rem',
                          fontWeight: 600,
                        }}>
                          Active
                        </span>
                      ) : session.status === 'voided' ? (
                        <span style={{
                          display: 'inline-block',
                          padding: '0.25rem 0.5rem',
                          borderRadius: '4px',
                          backgroundColor: 'rgba(107, 114, 128, 0.2)',
                          color: '#6b7280',
                          fontSize: '0.75rem',
                          fontWeight: 600,
                        }}>
                          Voided
                        </span>
                      ) : (
                        <span style={{
                          display: 'inline-block',
                          padding: '0.25rem 0.5rem',
                          borderRadius: '4px',
                          backgroundColor: 'rgba(34, 197, 94, 0.2)',
                          color: 'var(--color-green)',
                          fontSize: '0.75rem',
                          fontWeight: 600,
                        }}>
                          Done
                        </span>
                      )}
                    </td>
                    <td style={{ textAlign: 'right' }}>{formatCurrency(session.billAmount)}</td>
                    <td style={{ textAlign: 'right' }}>
                      {session.status === 'completed' ? formatCurrency(session.amountReceived || 0) : '-'}
                    </td>
                    <td style={{ textAlign: 'right' }}>
                      {session.status === 'completed' ? formatCurrency(session.tipAmount || 0) : '-'}
                    </td>
                    {event.askCustomerAmount && (
                      <td style={{ textAlign: 'right' }}>
                        {session.customerPaidAmount !== undefined ? formatCurrency(session.customerPaidAmount) : '-'}
                      </td>
                    )}
                    {event.askCustomerRating && (
                      <td style={{ textAlign: 'center' }}>
                        {session.customerRating ? (
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.25rem' }}>
                            <span style={{ color: '#f59e0b' }}>{session.customerRating}</span>
                            <span style={{ color: '#f59e0b', fontSize: '0.875rem' }}>{'\u2605'}</span>
                          </span>
                        ) : '-'}
                      </td>
                    )}
                    {isSuperAdmin && (
                      <td style={{ textAlign: 'center' }}>
                        {session.status !== 'voided' && (
                          <div style={{ display: 'flex', gap: '0.25rem', justifyContent: 'center' }}>
                            <button
                              onClick={(e) => { e.stopPropagation(); openEditModal(session); }}
                              disabled={actionInProgress !== null}
                              style={{
                                padding: '0.25rem 0.5rem',
                                fontSize: '0.75rem',
                                backgroundColor: '#3b82f6',
                                color: '#fff',
                                border: 'none',
                                borderRadius: '4px',
                                cursor: 'pointer',
                                opacity: actionInProgress ? 0.5 : 1,
                              }}
                            >
                              Edit
                            </button>
                            <button
                              onClick={(e) => { e.stopPropagation(); voidSession(session); }}
                              disabled={actionInProgress !== null}
                              style={{
                                padding: '0.25rem 0.5rem',
                                fontSize: '0.75rem',
                                backgroundColor: '#ef4444',
                                color: '#fff',
                                border: 'none',
                                borderRadius: '4px',
                                cursor: 'pointer',
                                opacity: actionInProgress ? 0.5 : 1,
                              }}
                            >
                              {actionInProgress === `void-${session.id}` ? '...' : 'Void'}
                            </button>
                          </div>
                        )}
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
          {allSessions.length === 0 && (
            <p style={{ textAlign: 'center', color: 'var(--color-text-secondary)', padding: '2rem' }}>
              No sessions yet
            </p>
          )}
        </section>
      )}

      {/* Session Details (when filtered) */}
      {(selectedDate || selectedTherapist) && filteredSessions.length > 0 && (
        <section className="report-section">
          <h3>Sessions ({filteredSessions.length})</h3>
          <table className="report-table">
            <thead>
              <tr>
                <th>Time</th>
                <th style={{ textAlign: 'right' }}>Table</th>
                <th style={{ textAlign: 'right' }}>Seat</th>
                {!selectedTherapist && <th style={{ width: '50px' }}></th>}
                {!selectedTherapist && <th>Therapist</th>}
                <th style={{ textAlign: 'right' }}>Duration</th>
                <th style={{ textAlign: 'right' }}>Bill ({currencySymbol})</th>
                <th style={{ textAlign: 'right' }}>Received ({currencySymbol})</th>
                <th style={{ textAlign: 'right' }}>Tip ({currencySymbol})</th>
                {event.askCustomerAmount && <th style={{ textAlign: 'right' }}>Cust. Paid ({currencySymbol})</th>}
                {event.askCustomerRating && <th style={{ textAlign: 'center' }}>Rating</th>}
              </tr>
            </thead>
            <tbody>
              {filteredSessions.map((session) => {
                const therapist = therapists.find((t) => t.uid === session.therapistId);
                return (
                  <tr key={session.id}>
                    <td>
                      {session.completedAt?.toDate().toLocaleString('en-US', {
                        month: 'short',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit',
                      })}
                    </td>
                    <td style={{ textAlign: 'right' }}>{session.tableNo}</td>
                    <td style={{ textAlign: 'right' }}>{session.seatNo}</td>
                    {!selectedTherapist && (
                      <td>
                        {(therapist?.thumbnailUrl || therapist?.photoUrl) ? (
                          <img
                            src={therapist.thumbnailUrl || therapist.photoUrl}
                            alt=""
                            style={{
                              width: '32px',
                              height: '32px',
                              borderRadius: '50%',
                              objectFit: 'cover',
                            }}
                          />
                        ) : (
                          <div style={{
                            width: '32px',
                            height: '32px',
                            borderRadius: '50%',
                            backgroundColor: 'var(--color-bg-tertiary)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '0.75rem',
                            fontWeight: 600,
                          }}>
                            {therapist?.displayName?.charAt(0) || '?'}
                          </div>
                        )}
                      </td>
                    )}
                    {!selectedTherapist && <td>{therapist?.displayName || 'Unknown'}</td>}
                    <td style={{ textAlign: 'right' }}>{formatDuration(session.totalMinutes)}</td>
                    <td style={{ textAlign: 'right' }}>{formatCurrency(session.billAmount)}</td>
                    <td style={{ textAlign: 'right' }}>{formatCurrency(session.amountReceived || 0)}</td>
                    <td style={{ textAlign: 'right' }}>{formatCurrency(session.tipAmount || 0)}</td>
                    {event.askCustomerAmount && (
                      <td style={{ textAlign: 'right' }}>
                        {session.customerPaidAmount !== undefined ? formatCurrency(session.customerPaidAmount) : '-'}
                      </td>
                    )}
                    {event.askCustomerRating && (
                      <td style={{ textAlign: 'center' }}>
                        {session.customerRating ? (
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.25rem' }}>
                            <span style={{ color: '#f59e0b' }}>{session.customerRating}</span>
                            <span style={{ color: '#f59e0b', fontSize: '0.875rem' }}>{'\u2605'}</span>
                          </span>
                        ) : '-'}
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </section>
      )}

      {/* Edit Session Modal */}
      {editingSession && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
        }}>
          <div style={{
            backgroundColor: 'var(--color-bg)',
            borderRadius: '12px',
            padding: '1.5rem',
            maxWidth: '400px',
            width: '90%',
            maxHeight: '90vh',
            overflow: 'auto',
          }}>
            <h3 style={{ marginTop: 0, marginBottom: '1rem' }}>Edit Session</h3>

            <div style={{ marginBottom: '1rem', fontSize: '0.875rem', color: 'var(--color-text-secondary)' }}>
              <div>Therapist: {therapists.find(t => t.uid === editingSession.therapistId)?.displayName || 'Unknown'}</div>
              <div>Table {editingSession.tableNo}, Seat {editingSession.seatNo}</div>
              <div>Original: {editingSession.totalMinutes}m, {currencySymbol}{editingSession.amountReceived || 0}</div>
            </div>

            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', marginBottom: '0.25rem', fontWeight: 500 }}>
                Duration (minutes)
              </label>
              <input
                type="number"
                value={editMinutes}
                onChange={(e) => setEditMinutes(Number(e.target.value))}
                style={{
                  width: '100%',
                  padding: '0.5rem',
                  borderRadius: '6px',
                  border: '1px solid var(--color-border)',
                  backgroundColor: 'var(--color-bg-secondary)',
                  color: 'var(--color-text)',
                }}
              />
            </div>

            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', marginBottom: '0.25rem', fontWeight: 500 }}>
                Amount Received ({currencySymbol})
              </label>
              <input
                type="number"
                value={editAmountReceived}
                onChange={(e) => setEditAmountReceived(Number(e.target.value))}
                style={{
                  width: '100%',
                  padding: '0.5rem',
                  borderRadius: '6px',
                  border: '1px solid var(--color-border)',
                  backgroundColor: 'var(--color-bg-secondary)',
                  color: 'var(--color-text)',
                }}
              />
            </div>

            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', marginBottom: '0.25rem', fontWeight: 500 }}>
                Reason for Edit <span style={{ color: '#ef4444' }}>*</span>
              </label>
              <textarea
                value={editReason}
                onChange={(e) => setEditReason(e.target.value)}
                placeholder="e.g., Correcting duration error, fixing payment amount..."
                rows={2}
                style={{
                  width: '100%',
                  padding: '0.5rem',
                  borderRadius: '6px',
                  border: '1px solid var(--color-border)',
                  backgroundColor: 'var(--color-bg-secondary)',
                  color: 'var(--color-text)',
                  resize: 'vertical',
                }}
              />
            </div>

            <div style={{
              fontSize: '0.875rem',
              padding: '0.75rem',
              backgroundColor: 'var(--color-bg-secondary)',
              borderRadius: '6px',
              marginBottom: '1rem',
            }}>
              <div style={{ fontWeight: 500, marginBottom: '0.5rem' }}>Preview:</div>
              <div>New Bill: {currencySymbol}{(editMinutes * (event.pricePerMinute || 0)).toFixed(0)}</div>
              <div>New Tip: {currencySymbol}{Math.max(0, editAmountReceived - editMinutes * (event.pricePerMinute || 0)).toFixed(0)}</div>
            </div>

            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <button
                onClick={() => setEditingSession(null)}
                style={{
                  flex: 1,
                  padding: '0.75rem',
                  borderRadius: '6px',
                  border: '1px solid var(--color-border)',
                  backgroundColor: 'transparent',
                  color: 'var(--color-text)',
                  cursor: 'pointer',
                }}
              >
                Cancel
              </button>
              <button
                onClick={saveEditedSession}
                disabled={!editReason.trim() || actionInProgress !== null}
                style={{
                  flex: 2,
                  padding: '0.75rem',
                  borderRadius: '6px',
                  border: 'none',
                  backgroundColor: '#3b82f6',
                  color: '#fff',
                  cursor: 'pointer',
                  opacity: (!editReason.trim() || actionInProgress) ? 0.5 : 1,
                }}
              >
                {actionInProgress === `edit-${editingSession.id}` ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
